import { useRouter } from 'next/router';
import { memo, useCallback } from 'react';

import { useTrackUpgradeButtonClicked } from '@air/analytics';
import { Badge } from '@air/primitive-badge';
import { useAirModal } from '@air/provider-modal';

import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { isSubscriptionCheckoutRoute } from '~/constants/routes';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';

export const WorkspaceLibraryUpsellCard = memo(() => {
  const router = useRouter();
  const { data: subscription } = useSubscriptionContext();
  const [showPlansModal] = useAirModal(PlansModal);
  const { trackUpgradeButtonClicked } = useTrackUpgradeButtonClicked();
  const isOnCheckoutPage = isSubscriptionCheckoutRoute(router.pathname);

  const onClick = useCallback(() => {
    trackUpgradeButtonClicked({
      plan: subscription?.plan || 'basic',
      page: 'workspace_sidebar',
      location: 'sidebar_upsell_card',
    });
    showPlansModal();
  }, [trackUpgradeButtonClicked, subscription?.plan, showPlansModal]);

  if (!subscription?.isInFreePlan || isOnCheckoutPage) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-1 mb-2 block w-full rounded-6 border border-grey-4 bg-grey-2 p-3 text-left transition-colors hover:border-grey-5 hover:bg-grey-3 focus:outline-none focus:ring-2 focus:ring-blue-8 focus:ring-offset-2"
      data-testid="WORKSPACE_LIBRARY_UPSELL_CARD"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-12 text-grey-12" data-testid="WORKSPACE_LIBRARY_UPSELL_CARD_TITLE">
          Level up your library
        </span>
        <Badge
          appearance="pill"
          color="blue"
          size="small"
          className="shrink-0 whitespace-nowrap"
          data-testid="WORKSPACE_LIBRARY_UPSELL_CARD_CTA"
        >
          TRY IT OUT
        </Badge>
      </div>
      <p className="mt-1 text-12 text-grey-11">
        Upgrade for unlimited storage, advanced sharing, and AI creative tools.
      </p>
    </button>
  );
});

WorkspaceLibraryUpsellCard.displayName = 'WorkspaceLibraryUpsellCard';
